import { isJobAvailable } from "admin/organization/pricing_plan_utils";
import { getJob, getJobs } from "admin/rest_api";
import features from "features";
import { useEffectOnlyOnce, usePolling, useWkSelector } from "libs/react_hooks";
import noop from "lodash-es/noop";
import { useState } from "react";
import type { APIDataStore, APIJob, APIJobCommand } from "types/api_types";

type JobInfo = [jobKey: string, jobId: string];

// Whether a worker of the data store supports the job and the pricing plan of the active organization allows it
export function useIsJobAvailable(
  dataStore: APIDataStore | null | undefined,
  jobCommand: APIJobCommand,
): boolean {
  const activeOrganization = useWkSelector((state) => state.activeOrganization);
  return dataStore != null && isJobAvailable(dataStore, jobCommand, activeOrganization);
}

export function useStartAndPollJob({
  onSuccess = noop,
  onFailure = noop,
  initialJobKeyExtractor,
  interval = 2000,
}: {
  onSuccess?: (job: APIJob) => void;
  onFailure?: (job: APIJob) => void;
  initialJobKeyExtractor?: (job: APIJob) => string | null;
  interval?: number;
}): {
  runningJobs: Array<JobInfo>;
  startJob: ((startJobFn: () => Promise<JobInfo>) => Promise<void>) | null;
  mostRecentSuccessfulJob: APIJob | null;
} {
  const areJobsEnabled = features().jobsEnabled;

  const [runningJobs, setRunningJobs] = useState<Array<JobInfo>>([]);
  const [mostRecentSuccessfulJob, setMostRecentSuccessfulJob] = useState<APIJob | null>(null);

  useEffectOnlyOnce(() => {
    if (initialJobKeyExtractor != null && areJobsEnabled) {
      (async () => {
        const jobs = await getJobs();
        jobs.sort((a, b) => b.created - a.created); // sort in descending order
        for (const job of jobs) {
          const key = initialJobKeyExtractor(job);
          if (key != null && job.state === "SUCCESS") {
            setMostRecentSuccessfulJob(job);
            break;
          }
        }
      })();
    }
  });

  async function checkForJobs() {
    for (const [, jobId] of runningJobs) {
      const job = await getJob(jobId);
      if (job.state === "SUCCESS") {
        onSuccess(job);
        setRunningJobs((previous) => previous.filter(([, j]) => j !== jobId));
        if (mostRecentSuccessfulJob == null || job.created > mostRecentSuccessfulJob.created) {
          setMostRecentSuccessfulJob(job);
        }
      } else if (job.state === "FAILURE") {
        onFailure(job);
        setRunningJobs((previous) => previous.filter(([, j]) => j !== jobId));
      }
    }
  }

  usePolling(checkForJobs, runningJobs.length > 0 ? interval : null);

  return {
    runningJobs,
    startJob: areJobsEnabled
      ? async (startJobFn) => {
          const [key, jobId] = await startJobFn();
          setRunningJobs((previous) => [...previous, [key, jobId]]);
        }
      : null,
    mostRecentSuccessfulJob,
  };
}
